/*/*
 * This plugin uses a decoration set to keep track of valid places to break the
 * text when reflowing across page boundaries.
 *
 * Can be visualized using the ?debug=split-points flag
 *
 */

import { Node } from 'prosemirror-model'
import { EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

import { NodeTypeMap } from '@showrunner/codex'

import { isDialogueContdsEnabled } from './configData'
import { PluginFactory } from './types'

import styles from './split-points.module.css'

export const key = new PluginKey<State>('split-points')

export type State = DecorationSet

const EXCLUDED_BLOCK_TYPES: string[] = [
  NodeTypeMap.ACTION,
  NodeTypeMap.CHARACTER,
  NodeTypeMap.PARENTHETICAL,
  NodeTypeMap.SCENE_HEADING,
  NodeTypeMap.TRANSITION,
]
// A valid page boundary is a period, an exclamation pont, or a question mark.
// Additionally, they should have a space after the punctuation, so we don't
// capture each dot of "..."
// There must be non-whitespace following the space so that
// trailing whitespace doesn't register as a split point.
const REGEX = /[.!?]\s+(?=\S)/g

export const pluginFactory: PluginFactory = ({ mst }) => {
  return plugin({
    isDebugEnabled: () =>
      mst.view.isDebugEnabled('split-points') && !mst.view.useNewPaginator,
  })
}

export function plugin(settings: { isDebugEnabled: () => boolean }) {
  return new Plugin<State>({
    key,
    state: {
      init(_config, editorState) {
        if (isDialogueContdsEnabled(editorState)) {
          return allSplitPoints(editorState)
        }
        return DecorationSet.empty
      },
      apply(tr, state, oldState, newState) {
        // If not enabled don't compute any split points
        const isEnabled = isDialogueContdsEnabled(newState)
        if (!isEnabled) return DecorationSet.empty

        // If recently enabled, don't try to incrementally compute, build from scratch
        const wasEnabled = isDialogueContdsEnabled(oldState)
        if (!wasEnabled) return allSplitPoints(newState)

        const { doc } = tr

        // Feed positions from the transaction through the decoration set
        const decorationSet = state.map(tr.mapping, doc)

        // tr.changedRange() returns a single from-to pair in tr.doc coordinates
        // covering all content changes (or null for mark-only / empty
        // transactions). It already handles the multi-step "intermediate doc
        // was larger" case that we used to have to map intermediate steps for
        const range = tr.changedRange()
        if (!range) return decorationSet

        const $from = doc.resolve(range.from)
        const $to = doc.resolve(range.to)
        const nodeStart = $from.start($from.depth)
        const nodeEnd = $to.end($to.depth)

        return decorationSet
          .remove(decorationSet.find(nodeStart, nodeEnd))
          .add(doc, decorationsForRange(doc, nodeStart, nodeEnd))
      },
    },
    props: {
      decorations(state) {
        if (settings.isDebugEnabled()) return this.getState(state)
      },
    },
  })
}

function allSplitPoints(editorState: EditorState) {
  const { doc } = editorState
  const decorations = decorationsForRange(doc, 0, doc.content.size)
  return DecorationSet.create(doc, decorations)
}

export function decorationsForRange(doc: Node, from: number, to: number) {
  const decorations: Decoration[] = []

  doc.nodesBetween(from, to, (node, position) => {
    // `return false` here tells prosemirror to stop descending into children
    if (EXCLUDED_BLOCK_TYPES.includes(node.type.name)) return false

    // Mark non-excluded block nodes as split points
    if (node.isBlock) {
      decorations.push(
        Decoration.node(
          position,
          position + node.nodeSize,
          { class: styles.splitPoint },
          { kind: 'block' },
        ),
      )
      return
    }

    if (!node.isText || node.text === undefined) return

    let match
    REGEX.lastIndex = 0 // Reset regex state

    while ((match = REGEX.exec(node.text)) !== null) {
      const matchPosition = position + match.index
      const from = matchPosition + 1
      const to = matchPosition + match[0].length
      decorations.push(
        Decoration.inline(
          from,
          to,
          { class: styles.splitPoint },
          { kind: 'inline' },
        ),
      )
    }
  })

  return decorations
}
